import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Download, FileText, Loader2, Lock, AlertCircle,
  ChevronLeft, ChevronRight, Pencil, Save, X,
  Building2, Factory, Utensils, Stethoscope, ShoppingBag,
  Wrench, Laptop, HardHat, Briefcase, ImageIcon, ArrowLeft,
} from "lucide-react";
import { formatCurrency } from "@/utils/financial-analysis";
import html2pdf from "html2pdf.js";

const NAVY = "#1D314D";
const GOLD = "#CEAB65";

// ─── Industry fallback visuals (no external stock photos — gradient + icon) ──
const INDUSTRY_VISUALS: Record<string, { gradient: string; icon: any; label: string }> = {
  manufacturing: { gradient: "from-slate-700 to-slate-900", icon: Factory, label: "Manufacturing" },
  technology: { gradient: "from-indigo-700 to-slate-900", icon: Laptop, label: "Technology" },
  healthcare: { gradient: "from-teal-700 to-slate-900", icon: Stethoscope, label: "Healthcare" },
  retail: { gradient: "from-amber-700 to-slate-900", icon: ShoppingBag, label: "Retail" },
  professional_services: { gradient: "from-blue-700 to-slate-900", icon: Briefcase, label: "Professional Services" },
  food_beverage: { gradient: "from-orange-700 to-slate-900", icon: Utensils, label: "Food & Beverage" },
  construction: { gradient: "from-yellow-800 to-slate-900", icon: HardHat, label: "Construction" },
  default: { gradient: "from-[#1D314D] to-slate-900", icon: Building2, label: "General Business" },
};

function getIndustryVisual(industry?: string) {
  const key = (industry || "").toLowerCase().replace(/[^a-z]/g, "_");
  return INDUSTRY_VISUALS[key] || INDUSTRY_VISUALS.default;
}

const translations = {
  en: {
    title: "Confidential Information Memorandum",
    downloadPdf: "Download PDF", downloading: "Generating PDF...",
    loading: "Loading CIM...", notAvailable: "CIM Not Available",
    notAvailableDesc: "This CIM is not yet available for viewing.",
    accessDenied: "Access Denied", accessDeniedDesc: "You do not have permission to view this CIM.",
    confidentialNotice: "CONFIDENTIAL — This document contains proprietary information",
    preparedBy: "Prepared by AcquizGroup™",
    edit: "Edit", save: "Save", cancel: "Cancel", saved: "Changes saved",
    page: "Page", of: "of",
    coverSubtitle: "Confidential Business Summary",
    notice: "Notice of Confidentiality",
    noticeBody: "This confidential business summary is intended to acquaint a prospective purchaser with preliminary information regarding the company whose business is currently available for acquisition. It is only furnished to prospective purchasers who have signed a Non-Disclosure Agreement. This document is not to be copied or distributed. AcquizGroup™ has not independently audited the information contained herein and makes no representation as to its accuracy or completeness. It is the responsibility of the buyer to independently verify all information provided.",
    opportunity: "Business Acquisition Opportunity",
    askingPrice: "Asking Price",
    highlights: "Business Acquisition Highlights",
    businessName: "Business Name", corporationName: "Corporation Name",
    location: "Location", salesLatest: "Sales (Latest Year)",
    discretionaryEarnings: "Discretionary Earnings", realEstate: "Real Estate",
    inventory: "Inventory", ffe: "Estimated FF&E", website: "Website",
    history: "History", yearsInBusiness: "years in business",
    reasonForSale: "Reason for Sale", employees: "Employees",
    financials: "Business Financials", category: "Category",
    revenue: "Revenue", ebitda: "EBITDA", addbacks: "Addbacks",
    sdeCalc: "SDE Calculation",
    addbacksItemized: "Addback Detail — for the buyer to assess individually",
    listingInfo: "Business Listing Information",
    industry: "Industry", entityType: "Entity Type", yearEstablished: "Year Established",
    fullTimeEmployees: "Full-Time Employees", partTimeEmployees: "Part-Time Employees",
    ownerHours: "Owner Hours/Week", nonCompete: "Non-Compete", licenses: "Licenses Required",
    faq: "Frequently Asked Questions",
    swot: "SWOT Analysis", strengths: "Strengths", weaknesses: "Weaknesses",
    opportunities: "Opportunities", threats: "Threats",
    process: "Business Acquisition Process",
    processSteps: [
      { title: "Initial Review", body: "Review this Confidential Summary and submit any follow-up questions to your AcquizGroup™ advisor." },
      { title: "NDA & Full Disclosure", body: "Sign a Non-Disclosure Agreement to receive the complete financial package and schedule a call with the seller." },
      { title: "Pre-Financing", body: "Registered lenders on the platform review anonymized deal data and may submit conditional pre-financing offers." },
      { title: "Offer & Negotiation", body: "Submit a Letter of Intent (LOI) once you are ready to move forward with a conditional offer." },
      { title: "Due Diligence", body: "Upon LOI signature by all parties, choose a lender and complete your due diligence review." },
      { title: "Closing", body: "Finalize financing, sign the purchase agreement, and take possession of the business." },
    ],
    about: "About AcquizGroup™",
    aboutBody: "AcquizGroup™ is a bilingual, AI-powered M&A marketplace connecting sellers, buyers, brokers, lenders, and closing professionals through a structured 11-step deal pipeline — built to help small and mid-sized businesses find the right buyer, with the right financing, on the right terms.",
    thankYou: "Thank You For Reading",
    contactUs: "For more information, please contact your AcquizGroup™ advisor.",
    noPhoto: "No image provided — industry placeholder shown",
    financingFeasibility: "Financing Feasibility (DSCR)",
    adjustedEarnings: "Adjusted Earnings",
    adjustedPrice: "Adjusted Purchase Price",
    loanAmount: "Estimated Loan Amount",
    annualDebtService: "Annual Debt Service",
    dscrLabel: "Debt Service Coverage Ratio (DSCR)",
    dscrTarget: "Target",
    notCompletedDscr: "Financing feasibility pre-evaluation not yet completed for this deal.",
    financingNotice: "This is a staff pre-evaluation estimate based on generic financing assumptions. Actual lender terms may differ.",
    downPayment: "Down Payment",
    interestRate: "Interest Rate",
    loanTerm: "Loan Term",
    years: "years",
  },
  fr: {
    title: "Mémorandum d'Information Confidentiel",
    downloadPdf: "Télécharger PDF", downloading: "Génération du PDF...",
    loading: "Chargement du CIM...", notAvailable: "CIM Non Disponible",
    notAvailableDesc: "Ce CIM n'est pas encore disponible.",
    accessDenied: "Accès Refusé", accessDeniedDesc: "Vous n'avez pas la permission de voir ce CIM.",
    confidentialNotice: "CONFIDENTIEL — Ce document contient des informations propriétaires",
    preparedBy: "Préparé par AcquizGroup™",
    edit: "Modifier", save: "Enregistrer", cancel: "Annuler", saved: "Modifications enregistrées",
    page: "Page", of: "sur",
    coverSubtitle: "Résumé d'Affaires Confidentiel",
    notice: "Avis de Confidentialité",
    noticeBody: "Ce résumé d'affaires confidentiel vise à informer un acheteur potentiel sur l'entreprise actuellement disponible pour acquisition. Il n'est remis qu'aux acheteurs ayant signé une entente de confidentialité. Ce document ne doit pas être copié ou distribué. AcquizGroup™ n'a pas vérifié de façon indépendante les informations contenues et ne fait aucune représentation quant à leur exactitude. Il incombe à l'acheteur de vérifier indépendamment toute l'information fournie.",
    opportunity: "Opportunité d'Acquisition",
    askingPrice: "Prix Demandé",
    highlights: "Points Saillants de l'Acquisition",
    businessName: "Nom de l'Entreprise", corporationName: "Nom de la Société",
    location: "Localisation", salesLatest: "Ventes (Dernière Année)",
    discretionaryEarnings: "Bénéfices Discrétionnaires", realEstate: "Immobilier",
    inventory: "Inventaire", ffe: "Équipement Estimé", website: "Site Web",
    history: "Historique", yearsInBusiness: "ans en affaires",
    reasonForSale: "Raison de la Vente", employees: "Employés",
    financials: "Finances de l'Entreprise", category: "Catégorie",
    revenue: "Revenu", ebitda: "EBITDA", addbacks: "Ajustements",
    sdeCalc: "Calcul SDE",
    addbacksItemized: "Détail des retraitements — à évaluer individuellement par l'acheteur",
    listingInfo: "Informations sur l'Annonce",
    industry: "Industrie", entityType: "Type d'Entité", yearEstablished: "Année de Fondation",
    fullTimeEmployees: "Employés Temps Plein", partTimeEmployees: "Employés Temps Partiel",
    ownerHours: "Heures Propriétaire/Sem", nonCompete: "Non-Concurrence", licenses: "Licences Requises",
    faq: "Foire Aux Questions",
    swot: "Analyse SWOT", strengths: "Forces", weaknesses: "Faiblesses",
    opportunities: "Opportunités", threats: "Menaces",
    process: "Processus d'Acquisition",
    processSteps: [
      { title: "Révision Initiale", body: "Consultez ce résumé confidentiel et soumettez vos questions à votre conseiller AcquizGroup™." },
      { title: "NDA & Divulgation Complète", body: "Signez une entente de confidentialité pour recevoir le dossier financier complet et planifier un appel avec le vendeur." },
      { title: "Pré-financement", body: "Les prêteurs inscrits sur la plateforme examinent les données anonymisées et peuvent soumettre des offres conditionnelles." },
      { title: "Offre & Négociation", body: "Soumettez une lettre d'intention (LOI) une fois prêt à avancer avec une offre conditionnelle." },
      { title: "Vérification Diligente", body: "Une fois la LOI signée par toutes les parties, choisissez un prêteur et complétez votre vérification diligente." },
      { title: "Clôture", body: "Finalisez le financement, signez le contrat d'achat et prenez possession de l'entreprise." },
    ],
    about: "À Propos d'AcquizGroup™",
    aboutBody: "AcquizGroup™ est une plateforme M&A bilingue propulsée par l'IA, connectant vendeurs, acheteurs, courtiers, prêteurs et professionnels de la clôture à travers un pipeline structuré en 11 étapes — conçue pour aider les PME à trouver le bon acheteur, avec le bon financement, aux bonnes conditions.",
    thankYou: "Merci de Votre Lecture",
    contactUs: "Pour plus d'informations, veuillez contacter votre conseiller AcquizGroup™.",
    noPhoto: "Aucune image fournie — visuel d'industrie affiché",
    financingFeasibility: "Faisabilité du Financement (DSCR)",
    adjustedEarnings: "Revenu Ajusté",
    adjustedPrice: "Prix d'Achat Ajusté",
    loanAmount: "Montant de Prêt Estimé",
    annualDebtService: "Service de Dette Annuel",
    dscrLabel: "Ratio de Couverture de la Dette (DSCR)",
    dscrTarget: "Cible",
    notCompletedDscr: "La pré-évaluation de faisabilité du financement n'a pas encore été complétée pour ce deal.",
    financingNotice: "Ceci est une estimation de pré-évaluation par le personnel basée sur des hypothèses de financement génériques. Les conditions réelles du prêteur peuvent différer.",
    downPayment: "Mise de Fonds",
    interestRate: "Taux d'Intérêt",
    loanTerm: "Durée du Prêt",
    years: "ans",
  },
};

interface CIMContent {
  executiveSummary?: string;
  businessOverview?: string;
  // AJOUTÉ — le vrai générateur NestJS (ai.service.ts generateCimNarrative())
  // écrit ce champ sous le nom "companyOverview", pas "businessOverview".
  // Les deux noms sont conservés dans l'interface pour couvrir un CIM
  // généré par l'ancien chemin client (cim-generation.service.ts, qui lui
  // écrit bien "businessOverview") — voir businessOverviewText plus bas
  // pour la résolution des deux.
  companyOverview?: string;
  financialHighlights?: string;
  growthOpportunities?: string;
  investmentThesis?: string;
  transitionPlan?: string;
  [key: string]: string | undefined;
}

interface DscrAnalysis {
  earnings?: number;
  adjustedPrice?: number;
  loanAmount?: number;
  annualDebtService?: number;
  dscr?: number;
  targetDscr?: number;
  financingAssumptions?: { downPayment?: number; interestRate?: number; termYears?: number };
}

interface CIMDataDoc {
  dealId: string;
  content: CIMContent;
  financialSummary?: {
    adjustedEbitda?: number;
    ebitdaMultiple?: number;
    valuationLow?: number;
    valuationMid?: number;
    valuationHigh?: number;
  };
  dscrAnalysis?: DscrAnalysis;
  status: string;
}

function fmt(n?: number) {
  return n ? formatCurrency(n) : "—";
}

export default function CIMPreview() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;
  const printRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [cimData, setCimData] = useState<CIMDataDoc | null>(null);
  const [fullDeal, setFullDeal] = useState<any>(null);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState<CIMContent>({});
  const [saving, setSaving] = useState(false);

  const isStaff = user?.roles?.includes("staff") || user?.roles?.includes("admin");
console.log('DEBUG role:', user?.role, '| roles:', JSON.stringify(user?.roles));

 useEffect(() => {
  if (dealId && user !== undefined) loadData();
}, [dealId, user]);

  const loadData = async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      setAccessDenied(false);
      const cimDoc = await getDoc(doc(db, "cimData", dealId));
      if (cimDoc.exists()) {
        const data = cimDoc.data() as CIMDataDoc;
        if (data.status !== "sent_to_client" && data.status !== "approved") {
          if (!isStaff) { setAccessDenied(true); return; }
        }
        setCimData(data);
        setEditedContent(data.content || {});
      }

      let dealSnap = await getDoc(doc(db, "dealSubmissions", dealId));
      if (!dealSnap.exists()) dealSnap = await getDoc(doc(db, "deals", dealId));
      if (dealSnap.exists()) {
        const data = dealSnap.data();
        setFullDeal(data);
console.log('DEBUG fullDeal:', data);
console.log('DEBUG uploadedDocuments:', data.uploadedDocuments);
console.log('DEBUG rawFormData:', data.rawFormData);
       if (data.referenceNumber) {
setReferenceNumber(data.referenceNumber);
        }
      }
    } catch (error) {
      console.error("Error loading CIM:", error);
      toast({ title: "Error", description: "Failed to load CIM", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const general = fullDeal?.rawFormData?.general || fullDeal?.general || {};
  const operations = fullDeal?.rawFormData?.operations || fullDeal?.operations || {};
  const financials = fullDeal?.rawFormData?.financials || fullDeal?.financials || {};
  const assets = fullDeal?.rawFormData?.assets || {};
  const realEstate = fullDeal?.rawFormData?.realEstate || {};
  const licensing = fullDeal?.rawFormData?.licensing || {};
  const swot = fullDeal?.rawFormData?.swot || {};
  const culture = fullDeal?.rawFormData?.culture || {};
  // AJOUTÉ — projections.growthOpportunities est un vrai champ du
  // formulaire (StepProjections.tsx, formData.projections.growthOpportunities),
  // pas seulement un texte généré par l'IA. Utilisé ci-dessous comme
  // repli quand la narrative IA (content.growthOpportunities, la même
  // clé côté cimData) est vide ou en échec — exactement le même principe
  // que businessOverviewText plus bas pour general.description.
  const projections = fullDeal?.rawFormData?.projections || {};

  const addbackItems: { description: string; amount: number }[] =
    fullDeal?.rawFormData?.addbacks?.items || [];
  const addbacksTotal = addbackItems.reduce(
    (sum: number, it: any) => sum + (Number(it?.amount) || 0),
    0,
  );

  const dscrAnalysis: DscrAnalysis | undefined = cimData?.dscrAnalysis || fullDeal?.dscrAnalysis;

  const companyName = general.tradeName || general.legalName || fullDeal?.businessName || "Company";

  // AJOUTÉ — le logo de l'entreprise (deal.logoUrl, écrit par
  // deals.service.ts submitDeal() côté NestJS depuis cette même session)
  // n'était jamais lu nulle part dans ce fichier — seules les "photos
  // d'entreprise" génériques (uploadedDocuments) alimentaient la page
  // couverture. Le logo est affiché séparément ci-dessous, en badge sur
  // la page de couverture, plutôt que mélangé aux photos.
  const companyLogo = fullDeal?.logoUrl;

  const photos = useMemo(() => {
    const docs = fullDeal?.uploadedDocuments || [];
    return docs.filter((d: any) => (d.mimeType || "").startsWith("image/"));
  }, [fullDeal]);

  const coverPhoto = photos[0]?.url;
  const secondaryPhoto = photos[1]?.url;
  const visual = getIndustryVisual(general.industry);
  const VisualIcon = visual.icon;

  const effectiveAskingPrice = financials.includeRealEstateInPrice
    ? (financials.totalAskingPriceWithRealEstate || financials.askingPrice)
    : financials.askingPrice;

  const sde = (financials.ebitdaYear1 || 0) + addbacksTotal;

  const startEdit = () => { setEditedContent(cimData?.content || {}); setEditMode(true); };
  const cancelEdit = () => { setEditedContent(cimData?.content || {}); setEditMode(false); };

  const saveEdits = async () => {
    if (!dealId) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "cimData", dealId),
        { content: editedContent, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setCimData(prev => (prev ? { ...prev, content: editedContent } : prev));
      setEditMode(false);
      toast({ title: t.saved });
    } catch (err) {
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const content = editMode ? editedContent : (cimData?.content || {});

  // AJOUTÉ — résout businessOverview vs companyOverview (voir le
  // commentaire sur l'interface CIMContent plus haut) : le générateur
  // NestJS actif écrit companyOverview, l'ancien chemin client (probable
  // code mort) écrivait businessOverview. On essaie les deux avant de
  // retomber sur la description brute du formulaire.
  const businessOverviewText = content.companyOverview || content.businessOverview || general.description || "";
  const businessOverviewEditValue = editedContent.companyOverview ?? editedContent.businessOverview ?? general.description ?? "";

  // AJOUTÉ — repli composé pour le Plan de Transition, reconstruit à
  // partir des vrais champs de l'étape 9 (Culture et Transition) déjà
  // collectés dans le formulaire — confirmé suffisant, pas besoin d'un
  // nouveau champ de texte libre dédié. Utilisé si la narrative IA
  // échoue ou est vide.
  const consultingLabels: Record<string, { fr: string; en: string }> = {
    yes_full_time: { fr: "Disponible à temps plein", en: "Available full-time" },
    yes_part_time: { fr: "Disponible à temps partiel", en: "Available part-time" },
    yes_on_demand: { fr: "Disponible sur demande", en: "Available on-demand" },
    negotiable: { fr: "Négociable selon l'acheteur", en: "Negotiable based on buyer" },
    yes: { fr: "Disponible", en: "Available" },
    no: { fr: "Non disponible", en: "Not available" },
  };
  const transitionPlanFallback = useMemo(() => {
    const isFr = language === "fr";
    const lines: string[] = [];
    if (culture.postSaleConsultingAvailability) {
      const label = consultingLabels[culture.postSaleConsultingAvailability];
      lines.push(
        `${isFr ? "Consultation post-vente" : "Post-sale consulting"}: ${label ? (isFr ? label.fr : label.en) : culture.postSaleConsultingAvailability}` +
        (culture.postSaleConsultingDetails ? ` — ${culture.postSaleConsultingDetails}` : "")
      );
    }
    if (culture.transitionDurationPreference) {
      lines.push(`${isFr ? "Durée de transition préférée" : "Preferred transition duration"}: ${culture.transitionDurationPreference}`);
    }
    if (culture.nonCompetePeriod) {
      lines.push(
        `${isFr ? "Période de non-concurrence" : "Non-compete period"}: ${culture.nonCompetePeriod}` +
        (culture.nonCompeteRadius ? ` (${culture.nonCompeteRadius} ${culture.nonCompeteRadiusUnit || "km"})` : "")
      );
    }
    if (culture.idealSuccessorDescription) {
      lines.push(`${isFr ? "Profil de successeur idéal" : "Ideal successor profile"}: ${culture.idealSuccessorDescription}`);
    }
    // CORRIGÉ — culture.growthGoals retiré de ce repli : fait doublon avec
    // growthOpportunities (étape 8), déjà affiché sur la page précédente
    // du CIM. currentChallenges n'a jamais été inclus ici non plus (déjà
    // couvert par l'analyse SWOT). Les deux champs restent dans le
    // formulaire (servent aussi StrategicPlanGenerator.tsx), seule leur
    // apparition redondante dans le CIM est retirée.
    if (culture.dealBreakersFromSeller) {
      lines.push(`${isFr ? "Conditions non négociables" : "Non-negotiable conditions"}: ${culture.dealBreakersFromSeller}`);
    }
    return lines.join("\n\n");
  }, [culture, language]);

  // ─── Image or industry-visual block ─────────────────────────────
  const ImageBlock = ({ url, className }: { url?: string; className?: string }) => {
    if (url) {
      return <img src={url} alt="" className={`object-cover ${className || ""}`} />;
    }
    return (
      <div className={`flex flex-col items-center justify-center gap-2 bg-gradient-to-br ${visual.gradient} text-white/70 ${className || ""}`}>
        <VisualIcon className="h-10 w-10" />
        <span className="text-[10px] uppercase tracking-wide">{t.noPhoto}</span>
      </div>
    );
  };

  // CORRIGÉ — accepte maintenant un repli optionnel sur une vraie donnée
  // du formulaire (rawFallback), pour les champs comme growthOpportunities
  // qui existent à la fois comme texte saisi par le vendeur
  // (formData.projections.growthOpportunities) ET comme narrative
  // générée par l'IA (content.growthOpportunities, même nom de clé côté
  // cimData) — sans ce repli, un échec de génération IA affichait le
  // texte de secours brut au lieu du texte réel que le vendeur avait
  // pourtant fourni.
  const EditableBlock = ({ field, label, rawFallback }: { field: keyof CIMContent; label: string; rawFallback?: string }) => (
    <section className="mb-8">
      <h2 className="text-xl font-bold border-b-2 pb-2 mb-4" style={{ borderColor: GOLD, color: NAVY }}>
        {label}
      </h2>
      {editMode ? (
        <Textarea
          value={editedContent[field] ?? rawFallback ?? ""}
          onChange={(e) => setEditedContent(prev => ({ ...prev, [field]: e.target.value }))}
          rows={6}
          className="text-sm"
        />
      ) : (
        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
          {content[field] || rawFallback || "—"}
        </p>
      )}
    </section>
  );

  // ─── Build pages ─────────────────────────────────────────────────
  const pages: { key: string; render: () => JSX.Element }[] = [
    {
      key: "cover",
      render: () => (
        <div className="h-full flex flex-col">
          <div className="p-10 text-white relative" style={{ background: NAVY }}>
            {/* AJOUTÉ — badge logo, affiché seulement si un logo a été
                uploadé (deal.logoUrl). Ne remplace pas le nom de
                l'entreprise, s'affiche à côté. */}
            {companyLogo && (
              <div
                className="absolute top-6 right-6 w-16 h-16 rounded-lg bg-white p-1.5 shadow-lg flex items-center justify-center overflow-hidden"
              >
                <img src={companyLogo} alt="" className="max-w-full max-h-full object-contain" />
              </div>
            )}
            <h1 className="text-4xl font-bold pr-20" style={{ color: GOLD }}>{companyName}</h1>
            <p className="text-lg mt-2 text-white/80">{t.coverSubtitle}</p>
          </div>
          <div className="flex-1 relative">
            <ImageBlock url={coverPhoto} className="w-full h-full absolute inset-0" />
          </div>
          <div className="p-6 text-center" style={{ background: NAVY }}>
            <p className="text-white/70 text-sm">{t.preparedBy}</p>
            {referenceNumber && <p className="text-xs font-mono text-white/40 mt-1">Ref: {referenceNumber}</p>}
          </div>
        </div>
      ),
    },
    {
      key: "confidentiality",
      render: () => (
        <div className="h-full p-10 flex flex-col justify-center" style={{ background: NAVY }}>
          <h2 className="text-2xl font-bold mb-6" style={{ color: GOLD }}>{t.notice}</h2>
          <p className="text-white/80 leading-relaxed text-sm">{t.noticeBody}</p>
        </div>
      ),
    },
    {
      key: "opportunity",
      render: () => (
        <div className="h-full grid grid-cols-2">
          <div className="p-8 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">{t.opportunity}</p>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NAVY }}>{companyName}</h2>
            {editMode ? (
              <Textarea
                value={businessOverviewEditValue}
                onChange={(e) => setEditedContent(prev => ({ ...prev, companyOverview: e.target.value }))}
                rows={8}
                className="text-sm"
              />
            ) : (
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                {businessOverviewText || "—"}
              </p>
            )}
            <div className="mt-8">
              <p className="text-xs uppercase tracking-widest text-gray-400">{t.askingPrice}</p>
              <p className="text-3xl font-bold" style={{ color: NAVY }}>{fmt(effectiveAskingPrice)}</p>
            </div>
          </div>
          <ImageBlock url={secondaryPhoto} className="h-full w-full" />
        </div>
      ),
    },
    {
      key: "highlights",
      render: () => (
        <div className="h-full p-10 bg-gray-50">
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: NAVY }}>{t.highlights}</h2>
          <div
            className="mx-auto max-w-3xl rounded-2xl p-8 grid grid-cols-2 gap-x-8 gap-y-5 bg-white"
            style={{ border: `3px solid ${GOLD}` }}
          >
            {[
              [t.businessName, general.legalName || companyName],
              [t.corporationName, general.legalName ? `${general.legalName}` : "—"],
              [t.askingPrice, fmt(effectiveAskingPrice)],
              [t.location, [general.locationCity, general.locationCountry].filter(Boolean).join(", ") || "—"],
              [t.salesLatest, fmt(financials.revenueYear1)],
              [t.discretionaryEarnings, fmt(sde)],
              [t.realEstate, realEstate?.isIncluded ? fmt(realEstate.realEstateAskPrice) : (realEstate?.monthlyRent ? `${fmt(realEstate.monthlyRent)}/mo` : "—")],
              [t.inventory, fmt(assets.inventoryValue)],
              [t.ffe, fmt(assets.ffeValue)],
              [t.website, general.website || "—"],
              [t.history, general.yearEstablished ? `${new Date().getFullYear() - general.yearEstablished} ${t.yearsInBusiness}` : "—"],
              [t.reasonForSale, general.reasonForSale || "—"],
            ].map(([label, value]) => (
              <div key={label as string}>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="font-semibold text-sm" style={{ color: NAVY }}>{value}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: "financials",
      render: () => (
        <div className="h-full p-10 overflow-y-auto">
          <h2 className="text-2xl font-bold mb-8" style={{ color: NAVY }}>{t.financials}</h2>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2" style={{ borderColor: NAVY }}>
                <th className="text-left py-2">{t.category}</th>
                <th className="text-right py-2">Year 1</th>
                <th className="text-right py-2">Year 2</th>
                <th className="text-right py-2">Year 3</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-medium">{t.revenue}</td>
                <td className="text-right">{fmt(financials.revenueYear1)}</td>
                <td className="text-right">{fmt(financials.revenueYear2)}</td>
                <td className="text-right">{fmt(financials.revenueYear3)}</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-medium">{t.ebitda}</td>
                <td className="text-right">{fmt(financials.ebitdaYear1)}</td>
                <td className="text-right">{fmt(financials.ebitdaYear2)}</td>
                <td className="text-right">{fmt(financials.ebitdaYear3)}</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-medium">{t.addbacks}</td>
                <td className="text-right">{fmt(addbacksTotal)}</td>
                <td className="text-right">—</td>
                <td className="text-right">—</td>
              </tr>
              <tr className="font-bold" style={{ color: NAVY }}>
                <td className="py-3">{t.sdeCalc}</td>
                <td className="text-right">{fmt(sde)}</td>
                <td className="text-right">—</td>
                <td className="text-right">—</td>
              </tr>
            </tbody>
          </table>

          {addbackItems.length > 0 && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-3">{t.addbacksItemized}</p>
              <table className="w-full text-xs border-collapse">
                <tbody>
                  {addbackItems.map((item, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 text-gray-600">{item.description || "—"}</td>
                      <td className="py-2 text-right font-medium" style={{ color: NAVY }}>{fmt(item.amount)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="py-2 font-semibold" style={{ color: NAVY }}>{t.addbacks}</td>
                    <td className="py-2 text-right font-semibold" style={{ color: NAVY }}>{fmt(addbacksTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "financing",
      render: () => (
        <div className="h-full p-10">
          <h2 className="text-2xl font-bold mb-8" style={{ color: NAVY }}>{t.financingFeasibility}</h2>
          {!dscrAnalysis ? (
            <p className="text-gray-500 text-sm">{t.notCompletedDscr}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400">{t.adjustedEarnings}</p>
                  <p className="text-2xl font-bold" style={{ color: NAVY }}>{fmt(dscrAnalysis.earnings)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400">{t.adjustedPrice}</p>
                  <p className="text-2xl font-bold" style={{ color: NAVY }}>{fmt(dscrAnalysis.adjustedPrice)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400">{t.loanAmount}</p>
                  <p className="text-lg font-semibold">{fmt(dscrAnalysis.loanAmount)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400">{t.annualDebtService}</p>
                  <p className="text-lg font-semibold">{fmt(dscrAnalysis.annualDebtService)}</p>
                </div>
              </div>

              <div
                className="rounded-2xl p-6 text-center mb-8"
                style={{
                  background:
                    (dscrAnalysis.dscr || 0) >= (dscrAnalysis.targetDscr || 1.3)
                      ? "#ECFDF5"
                      : (dscrAnalysis.dscr || 0) >= 1.0
                      ? "#FFFBEB"
                      : "#FEF2F2",
                  border: `2px solid ${GOLD}`,
                }}
              >
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t.dscrLabel}</p>
                <p className="text-4xl font-bold" style={{ color: NAVY }}>
                  {(dscrAnalysis.dscr || 0).toFixed(2)}x
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t.dscrTarget}: {(dscrAnalysis.targetDscr || 1.3).toFixed(1)}x
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm mb-8">
                <div>
                  <p className="text-gray-500">{t.downPayment}</p>
                  <p className="font-semibold">{dscrAnalysis.financingAssumptions?.downPayment ?? "—"}%</p>
                </div>
                <div>
                  <p className="text-gray-500">{t.interestRate}</p>
                  <p className="font-semibold">{dscrAnalysis.financingAssumptions?.interestRate ?? "—"}%</p>
                </div>
                <div>
                  <p className="text-gray-500">{t.loanTerm}</p>
                  <p className="font-semibold">
                    {dscrAnalysis.financingAssumptions?.termYears ?? "—"} {t.years}
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-400 italic">{t.financingNotice}</p>
            </>
          )}
        </div>
      ),
    },
    {
      key: "inventory",
      render: () => {
        const inventoryItems = fullDeal?.rawFormData?.assets?.inventoryItems || [];
        const inventoryDocs = fullDeal?.rawFormData?.assets?.assetListDocuments || [];
        if (inventoryItems.length === 0 && inventoryDocs.length === 0) return <div className="h-full" />;
        const inventoryTotal = inventoryItems.reduce(
          (sum: number, it: any) => sum + (Number(it.quantity) || 0) * (Number(it.unitValue) || 0),
          0,
        );
        return (
          <div className="h-full p-10 overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6" style={{ color: NAVY }}>
              {language === "fr" ? "Inventaire, équipement et machinerie" : "Inventory, Equipment & Machinery"}
            </h2>
            {inventoryItems.length > 0 ? (
              <table className="w-full text-sm border-collapse mb-6">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: NAVY }}>
                    <th className="text-left py-2">{language === "fr" ? "Description" : "Description"}</th>
                    <th className="text-left py-2">{language === "fr" ? "Catégorie" : "Category"}</th>
                    <th className="text-right py-2">{language === "fr" ? "Qté" : "Qty"}</th>
                    <th className="text-right py-2">{language === "fr" ? "Valeur unitaire" : "Unit Value"}</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryItems.map((item: any, i: number) => (
                    <tr key={i} className="border-b">
                      <td className="py-2">{item.description || "—"}</td>
                      <td className="py-2 text-gray-500">{item.category || "—"}</td>
                      <td className="py-2 text-right">{item.quantity || "—"}</td>
                      <td className="py-2 text-right">{fmt(item.unitValue)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold" style={{ color: NAVY }}>
                    <td className="py-3" colSpan={3}>{language === "fr" ? "Total inventaire" : "Inventory Total"}</td>
                    <td className="py-3 text-right">{fmt(inventoryTotal)}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-sm mb-6">
                {language === "fr" ? "Liste détaillée non fournie — voir documents ci-dessous." : "Detailed list not provided — see documents below."}
              </p>
            )}
            {inventoryDocs.length > 0 && (
              <div className="p-4 rounded-lg bg-gray-50 border" style={{ borderColor: GOLD }}>
                <p className="text-sm font-medium" style={{ color: NAVY }}>
                  {language === "fr"
                    ? `${inventoryDocs.length} document(s) original(aux) disponible(s)`
                    : `${inventoryDocs.length} original document(s) available`}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {language === "fr"
                    ? "Registre(s) d'actifs fourni(s) par le vendeur — accessible après signature du NDA via le Deal Room."
                    : 'Asset register(s) provided by the seller — accessible after NDA signature via the Deal Room.'}
                </p>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "listingInfo",
      render: () => (
        <div className="h-full p-10">
          <h2 className="text-2xl font-bold mb-6" style={{ color: NAVY }}>{t.listingInfo}</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              [t.industry, general.industry],
              [t.entityType, general.entityType],
              [t.yearEstablished, general.yearEstablished],
              [t.fullTimeEmployees, operations.fullTimeEmployees],
              [t.partTimeEmployees, operations.partTimeEmployees],
              [t.ownerHours, operations.ownerHoursPerWeek],
              [t.licenses, licensing.specialLicensesRequired ? "Yes" : "No"],
              [t.reasonForSale, general.reasonForSale],
            ].map(([label, value]) => (
              <div key={label as string} className="border-b pb-2">
                <span className="text-gray-500">{label}: </span>
                <span className="font-medium" style={{ color: NAVY }}>{value || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: "faq",
      render: () => {
        const faqs = [
          operations.ownerCriticalTasks && {
            q: language === "fr" ? "Quelles tâches le propriétaire effectue-t-il ?" : "What duties does the owner perform?",
            a: operations.ownerCriticalTasks,
          },
          operations.ownerAbsenceImpact && {
            q: language === "fr" ? "Quel est l'impact de l'absence du propriétaire ?" : "What is the impact if the owner is absent?",
            a: operations.ownerAbsenceImpact,
          },
          culture.managementTeamExplanation && {
            q: language === "fr" ? "Y a-t-il une équipe de gestion en place ?" : "Is there a management team in place?",
            a: culture.managementTeamExplanation,
          },
          operations.unionAffiliated && {
            q: language === "fr" ? "Le personnel est-il syndiqué ?" : "Is staff unionized?",
            a: operations.unionDetails || (language === "fr" ? "Oui" : "Yes"),
          },
        ].filter(Boolean) as { q: string; a: string }[];

        return (
          <div className="h-full p-10">
            <h2 className="text-2xl font-bold mb-6" style={{ color: NAVY }}>{t.faq}</h2>
            <div className="space-y-6">
              {faqs.length === 0 && <p className="text-gray-500 text-sm">—</p>}
              {faqs.map((f, i) => (
                <div key={i}>
                  <p className="font-semibold text-sm" style={{ color: NAVY }}>Q: {f.q}</p>
                  <p className="text-gray-700 text-sm mt-1">A: {f.a}</p>
                </div>
              ))}
            </div>
          </div>
        );
      },
    },
    {
      key: "swot",
      render: () => (
        <div className="h-full p-10">
          <h2 className="text-2xl font-bold mb-6" style={{ color: NAVY }}>{t.swot}</h2>
          <div className="grid grid-cols-2 gap-4 h-[calc(100%-60px)]">
            {[
              [t.strengths, swot.strengths, "bg-green-50"],
              [t.weaknesses, swot.weaknesses, "bg-red-50"],
              [t.opportunities, swot.opportunities, "bg-blue-50"],
              [t.threats, swot.threats, "bg-amber-50"],
            ].map(([label, value, bg]) => (
              <div key={label as string} className={`rounded-lg p-4 ${bg}`}>
                <p className="font-semibold text-sm mb-2" style={{ color: NAVY }}>{label}</p>
                <p className="text-xs text-gray-700 whitespace-pre-wrap">{(value as string) || "—"}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: "growth",
      render: () => (
        <div className="h-full p-10">
          {/* CORRIGÉ — investmentThesis retiré : aucun champ formulaire
              équivalent, purement une composition IA sans donnée brute de
              secours, confirmée superflue par la revue du CIM. */}
          <EditableBlock field="growthOpportunities" label={language === "fr" ? "Opportunités de Croissance" : "Growth Opportunities"} rawFallback={projections.growthOpportunities} />
        </div>
      ),
    },
    {
      key: "transition",
      render: () => (
        <div className="h-full p-10">
          <EditableBlock field="transitionPlan" label={language === "fr" ? "Plan de Transition" : "Transition Plan"} rawFallback={transitionPlanFallback} />
        </div>
      ),
    },
    {
      key: "process",
      render: () => (
        <div className="h-full p-10">
          <h2 className="text-2xl font-bold mb-8" style={{ color: NAVY }}>{t.process}</h2>
          <div className="space-y-5">
            {t.processSteps.map((s, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: NAVY, color: GOLD }}
                >
                  {i + 1}
                </div>
                <div>
                  <p className="font-semibold text-sm" style={{ color: NAVY }}>{s.title}</p>
                  <p className="text-sm text-gray-600">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: "thankyou",
      render: () => (
        <div className="h-full p-10 flex flex-col items-center justify-center text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ color: NAVY }}>{t.thankYou}</h2>
          {referenceNumber && <p className="font-mono text-xs text-gray-400 mt-4">Ref: {referenceNumber}</p>}
          <p className="text-xs text-gray-400 mt-2">© {new Date().getFullYear()} AcquizGroup™</p>
        </div>
      ),
    },
  ];

  const goNext = () => { if (currentPage < pages.length - 1) { setDirection("next"); setCurrentPage(p => p + 1); } };
  const goPrev = () => { if (currentPage > 0) { setDirection("prev"); setCurrentPage(p => p - 1); } };

  const handleDownloadPdf = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      await html2pdf()
        .set({
          margin: 0,
          filename: `CIM_${companyName.replace(/\s+/g, "_")}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: "css", after: ".cim-page" },
        })
        .from(printRef.current)
        .save();
      toast({ title: "PDF downloaded successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate PDF", variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" /><span className="ml-2">{t.loading}</span>
      </div>
    );
  }
  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Lock className="h-16 w-16 text-muted-foreground" />
        <h2 className="mt-4 text-2xl font-bold">{t.accessDenied}</h2>
        <p className="text-muted-foreground mt-2">{t.accessDeniedDesc}</p>
      </div>
    );
  }
  if (!cimData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <AlertCircle className="h-16 w-16 text-muted-foreground" />
        <h2 className="mt-4 text-2xl font-bold">{t.notAvailable}</h2>
        <p className="text-muted-foreground mt-2">{t.notAvailableDesc}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {isStaff && dealId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/staff/deals/${dealId}`)}
            className="mb-3 text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === "fr" ? "Retour au dossier" : "Back to Deal"}
          </Button>
        )}
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6" style={{ color: NAVY }} />
            <h1 className="text-lg font-bold" style={{ color: NAVY }}>{t.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {isStaff && !editMode && (
              <Button size="sm" variant="outline" onClick={startEdit}>
                <Pencil className="h-4 w-4 mr-2" />{t.edit}
              </Button>
            )}
            {isStaff && editMode && (
              <>
                <Button size="sm" variant="outline" onClick={cancelEdit} disabled={saving}>
                  <X className="h-4 w-4 mr-2" />{t.cancel}
                </Button>
                <Button size="sm" onClick={saveEdits} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {t.save}
                </Button>
              </>
            )}
            <Button size="sm" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t.downloading}</>
                : <><Download className="h-4 w-4 mr-2" />{t.downloadPdf}</>}
            </Button>
          </div>
        </div>

        {/* Catalog / flip-book viewer */}
        <div
          className="relative rounded-lg shadow-2xl overflow-hidden bg-white"
          style={{ aspectRatio: "3 / 4" }}
        >
          <div
            key={currentPage}
            className="absolute inset-0"
            style={{
              animation: `${direction === "next" ? "cimSlideInRight" : "cimSlideInLeft"} 0.35s ease-out`,
            }}
          >
            {pages[currentPage].render()}
          </div>

          {/* Nav arrows */}
          <button
            onClick={goPrev}
            disabled={currentPage === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5" style={{ color: NAVY }} />
          </button>
          <button
            onClick={goNext}
            disabled={currentPage === pages.length - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-5 w-5" style={{ color: NAVY }} />
          </button>
        </div>

        {/* Page indicator */}
        <div className="flex items-center justify-center gap-2 mt-4">
          <span className="text-sm text-gray-600">
            {t.page} {currentPage + 1} {t.of} {pages.length}
          </span>
        </div>
        <div className="flex justify-center gap-1.5 mt-2">
          {pages.map((p, i) => (
            <button
              key={p.key}
              onClick={() => { setDirection(i > currentPage ? "next" : "prev"); setCurrentPage(i); }}
              className="w-2 h-2 rounded-full transition-colors"
              style={{ background: i === currentPage ? NAVY : "#d1d5db" }}
            />
          ))}
        </div>
      </div>

      {/* Hidden full-stack version for PDF export (all pages, no pagination) */}
      <div className="fixed -left-[9999px] top-0" aria-hidden="true">
        <div ref={printRef} style={{ width: "210mm" }}>
          {pages.map((p) => (
            <div key={p.key} className="cim-page" style={{ width: "210mm", height: "297mm", position: "relative", overflow: "hidden" }}>
              {p.render()}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes cimSlideInRight {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes cimSlideInLeft {
          from { opacity: 0; transform: translateX(-24px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}